import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Grid,
} from '@mui/material';
import './offers.scss';
import { fetchPastOrderedProductsPaginated, Product as ProductType } from './action.ts';
import Heading from '../../Components/heading.tsx';
import SupervisorHeading from '../../../WebApp/Components/Heading/heading.tsx';

interface BuyAgainScreenProps {
  cartItems: ProductType[];
  onAdd: (product: ProductType) => void;
  onRemove: (product: ProductType) => void;
  onBack: () => void;
  userPhone: string;
  userRole?: string;
}

const BuyAgainScreen: React.FC<BuyAgainScreenProps> = ({
  cartItems,
  onAdd,
  onRemove,
  onBack,
  userPhone,
  userRole,
}) => {
  const [pastPurchases, setPastPurchases] = useState<ProductType[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [existingProductIds, setExistingProductIds] = useState<Set<number>>(new Set());

  const loadPastPurchases = useCallback(async () => {
    if (!userPhone || loading || !hasNextPage) return;

    setLoading(true);
    try {
      const { products, hasNextPage: next, existingProductIds: updatedIds } =
        await fetchPastOrderedProductsPaginated(
          userPhone,
          page,
          8,
          existingProductIds
        );

      setPastPurchases(prev => [...prev, ...products]);
      setExistingProductIds(updatedIds);
      setHasNextPage(next);
      setPage(prev => prev + 1);
    } catch (error) {
      console.error('Failed to fetch past purchases', error);
    } finally {
      setLoading(false);
    }
  }, [userPhone, page, loading, hasNextPage, existingProductIds]);

  useEffect(() => {
    loadPastPurchases();
  }, [userPhone, loadPastPurchases]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (!target) return;

    const { scrollTop, clientHeight, scrollHeight } = target;

    if (scrollTop + clientHeight >= scrollHeight - 20 && !loading && hasNextPage) {
      loadPastPurchases();
    }
  };

  const totalProducts = pastPurchases.length;
  const isOdd = totalProducts % 2 !== 0;
const isVendorSupervisor = ["vendor_supervisor", "vendor_company_admin", "client_company_admin"].includes(userRole);

  if (isVendorSupervisor) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            flexShrink: 0,
            position: "sticky",
            top: 10,
            zIndex: 1100,     
          }}
        >
          <SupervisorHeading
            title="My Past Purchases"
            closeIcon="false"
            backArrow={true}
            onBack={onBack}
            className="no-margin-heading"
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pt:1.5,
            px: 2,
            pb: 'calc(56px + 20px)',
            background: '#f8fafb',
            minHeight: 0,
          }}
          onScroll={handleScroll} 
        >
          {pastPurchases.length === 0 && !loading && (
            <Typography sx={{ mt: 2 }}>No past purchases yet.</Typography>
          )}

          <Grid container spacing={1}>
            {pastPurchases.map((product) => {
              const inCart = cartItems.find((item) => item.id === product.id);
              return (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2}
                  key={product.id}
                >
                  <Box
                    className="buyagain-product"
                    sx={{
                      p: 2,
                      width: '100%',
                      margin: 'auto',
                      minHeight: 250,
                    }}
                  >
                    {product.discountPercent > 0 && (
                      <Box className="buyagain-discount-badge">
                        {product.discountPercent}% off
                      </Box>
                    )}
                    <img
                      src={product.image}
                      alt={product.name}
                      width="100%"
                      height={150}
                      style={{ objectFit: "contain" }}
                    />
                    <Typography fontSize={14} fontWeight={600} textAlign="center" mt={1}>
                      {product.name}
                    </Typography>
                    <Typography fontSize={14}>
                      ₹{product.price}
                      {product.originalPrice && product.originalPrice > product.price && (
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#888",
                            marginLeft: 6,
                            fontSize: 13,
                          }}
                        >
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </Typography>

                    {!inCart ? (
                      <button
                        className="buyagain-add-btn"
                        onClick={() => onAdd({ ...product, quantity: 1 })}
                      >
                        ADD
                      </button>
                    ) : (
                      <div className="buyagain-counter">
                        <button onClick={() => onRemove(product)}>-</button>
                        <span>{inCart.quantity}</span>
                        <button onClick={() => onAdd(product)}>+</button>
                      </div>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>


  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
    {loading && <Typography>Loading Products...</Typography>}
    {!hasNextPage && !loading && pastPurchases.length > 0 && (
      <Typography>No more products</Typography>
    )}
  </Box>
        </Box>
      </Box>
    );
  }
  return (
  <Container maxWidth="sm" sx={{ px: 2, pb: '90px' }}>
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 100,
        pt: '30px',
        pb: '8px',
      }}
    >
      <Heading title="Your Past Purchases" onBackClick={onBack} />
    </Box>
    <Box
      sx={{
        overflowY: 'auto',
        pr: 1,
      }}
      onScroll={handleScroll}
    >
      {pastPurchases.length === 0 && !loading && (
        <Typography>No past purchases yet.</Typography>
      )}

      <Grid container spacing={2} justifyContent="flex-start">
        {pastPurchases.map((product, index) => {
          const inCart = cartItems.find((item) => item.id === product.id);
          const isLastAndOdd = isOdd && index === totalProducts - 1;

          return (
            <Grid
              item
              xs={6}
              key={product.id}
              sx={{
                display: 'flex',
                justifyContent: isLastAndOdd ? 'flex-start' : 'center',
              }}
            >
              <Box className="buyagain-product">
                {product.discountPercent > 0 && (
                  <Box className="buyagain-discount-badge">
                    {product.discountPercent}% off
                  </Box>
                )}
                <img
                  src={product.image}
                  alt={product.name}
                  width={100}
                  height={100}
                  style={{ objectFit: 'contain' }}
                />
                <Typography fontSize={14} fontWeight={600} textAlign="center" mt={1}>
                  {product.name}
                </Typography>
                <Typography fontSize={14}>
                  ₹{product.price}
                  {product.originalPrice && product.originalPrice > product.price && (
                    <span
                      style={{
                        textDecoration: 'line-through',
                        color: '#888',
                        marginLeft: 6,
                        fontSize: 13,
                      }}
                    >
                      ₹{product.originalPrice}
                    </span>
                  )}
                </Typography>

                {!inCart ? (
                  <button
                    className="buyagain-add-btn"
                    onClick={() => onAdd({ ...product, quantity: 1 })}
                  >
                    ADD
                  </button>
                ) : (
                  <div className="buyagain-counter">
                    <button onClick={() => onRemove(product)}>-</button>
                    <span>{inCart.quantity}</span>
                    <button onClick={() => onAdd(product)}>+</button>
                  </div>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading && <Typography>Loading Products...</Typography>}
        {!hasNextPage && !loading && pastPurchases.length > 0 && (
          <Typography>No more products</Typography>
        )}
      </Box>
    </Box>
  </Container>
);
};

export default BuyAgainScreen;
